// The heard angle: the crank angle whose sound reaches the listener when the
// frame being drawn appears (CONTEXT.md, ADR 0002). It replaces the stand-in of
// #31 (the newest telemetry record carried forward), which ran ahead of the
// sound by the whole output latency.
//
// For each animation frame:
//
//   heard frame = heard time · sampleRate − latency_frames          (heard-clock.ts)
//   voice frame = the voice's sample the context played at that frame (telemetry-track.ts)
//   angle, rate and slow state at that voice frame                  (telemetry-track.ts)
//
// latency_frames is the voice's own lag between its shaft state and its audio
// (ready.latencyFrames). Muting is a gain of 0 after the voice (AudioSession.setLevel),
// so the voice, its telemetry and the context's clock keep running and the
// scene stays in sync; only stopping the audio stops the crank.
//
// Everything a frame calls is allocation-free.

import type { AudioEngine } from "../audio/engine";
import type { TelemetryBatch } from "../audio/protocol";
import { HeardClock, type AudioClock, type HeardClockOptions, type HeardPath } from "./heard-clock";
import { TelemetryTrack, newTrackSample, type TrackSample } from "./telemetry-track";

/** What read() writes: the heard shaft state, and how it was found. */
export interface HeardSample extends TrackSample {
  /** The heard crank speed: how fast the heard angle moves, rad/s (0 with the audio off or paused). */
  omega: number;
  /** The context frame heard, and the voice frame it played (NaN with the audio off). */
  contextFrame: number;
  voiceFrame: number;
  /** "timestamp" or "fallback" while audio runs, "off" without it (the crank stands still). */
  path: HeardPath | "off";
}

export function newHeardSample(): HeardSample {
  return { ...newTrackSample(), omega: 0, contextFrame: Number.NaN, voiceFrame: Number.NaN, path: "off" };
}

/** The parts of AudioEngine the heard angle follows; tests pass a synthetic one. */
export interface TelemetrySource {
  readonly context: AudioClock;
  readonly ready: { latencyFrames: number } | null;
  onTelemetry(listener: (batch: TelemetryBatch) => void): () => void;
}

export class HeardAngle {
  readonly clock: HeardClock;
  private track: TelemetryTrack | null = null;
  private source: TelemetrySource | null = null;
  private off: (() => void) | null = null;
  /** The angle shown last, kept while the audio is off. */
  private lastTheta = 0;
  private lastCycle = 0;

  constructor(options: HeardClockOptions = {}) {
    this.clock = new HeardClock(options);
  }

  /** The telemetry kept for the running voice, or null with the audio off. */
  get telemetry(): TelemetryTrack | null {
    return this.track;
  }

  /** Follows a running engine's telemetry and clock, or none (null) when the audio stops. */
  attach(source: TelemetrySource | AudioEngine | null): void {
    if (source === this.source) return;
    this.off?.();
    this.off = null;
    this.source = source;
    if (source === null) {
      this.track = null;
      this.clock.attach(null);
      return;
    }
    const track = new TelemetryTrack(source.context.sampleRate);
    this.track = track;
    this.clock.attach(source.context);
    this.off = source.onTelemetry((batch) => track.ingest(batch));
  }

  /** The heard shaft state for the frame drawn at `rafTime` (ms, performance clock), written into `out`. */
  read(rafTime: number, out: HeardSample): HeardSample {
    const source = this.source;
    const track = this.track;
    const time = this.clock.heardTime(rafTime);
    if (source === null || track === null || track.size === 0 || Number.isNaN(time)) {
      // No voice (or no telemetry yet): the crank stands where it stopped.
      out.theta = this.lastTheta;
      out.cycle = this.lastCycle;
      out.rate = 0;
      out.omega = 0;
      out.contextFrame = Number.NaN;
      out.voiceFrame = Number.NaN;
      out.path = "off";
      out.valid = false;
      return out;
    }
    const latency = source.ready?.latencyFrames ?? 0;
    out.contextFrame = time * track.sampleRate - latency;
    out.voiceFrame = track.voiceFrameAt(out.contextFrame);
    track.sample(out.voiceFrame, out);
    out.omega = out.rate;
    out.path = this.clock.path;
    this.lastTheta = out.theta;
    this.lastCycle = out.cycle;
    return out;
  }

  dispose(): void {
    this.attach(null);
  }
}
